import { useReducer, useCallback } from 'react'
import {
  compose,
  equals,
  filter,
  includes,
  isEmpty,
  join,
  map,
  pipe,
  prop,
  propSatisfies,
  sortBy,
  toLower,
} from 'ramda'
import debounce from 'lodash.debounce'
import effects from 'app/effects/effects'
import { checkEquals } from 'libs/globalFunc'
import {
  NORMAL,
  EVERYONE,
  SUBSCRIBER_WITHOUT_GROUP,
  SUB_GROUP,
  GROUP,
  EVERYONE_NAME,
  EVERY_GROUP_NAME,
  SUBSCRIBERS,
  WITHOUT_GROUP_NAME,
} from 'app/constants/recipient'
import { SCOPE_BEACON, SCOPE_GENERAL } from 'app/constants/scope'
import {
  PLACE_INVITATION_ACCEPT_INVITATION,
  PLACE_INVITATION_ACCEPT_REQUEST,
  PLACE_INVITATION_PUBLIC_SUBSCRIBE,
} from 'app/configs/statusConfigs'
import { TIMEOUT_SEARCH } from 'app/constants/compose'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import MessageAlert from 'app/components/MessageAlert'
import { AccessType } from 'app/auth/accessControlService'
import { useAccessControl } from 'app/auth/useAccessControl'

export const actionMessageType = {
  START_LOADING: 'START_LOADING',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  SET_SEARCH_RECIPIENT: 'SET_SEARCH_RECIPIENT',
  START_SEARCHING: 'START_SEARCHING',
  SET_SEARCH_TEXT: 'SET_SEARCH_TEXT',
}

const initialValue = () => {
  return {
    options: [],
    isLoading: false,
    isEmptyGroupAndSubscriber: false,
    searching: false,
    searchOptions: [],
    searchText: '',
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionMessageType.START_LOADING:
      return {
        ...state,
        isLoading: true,
      }
    case actionMessageType.FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        options: action.payload,
        isEmptyGroupAndSubscriber: action.payload.length === 0,
      }

    case actionMessageType.START_SEARCHING:
      return {
        ...state,
        searching: true,
      }

    case actionMessageType.SET_SEARCH_RECIPIENT:
      return {
        ...state,
        searching: false,
        searchOptions: action.value,
      }

    case actionMessageType.SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.payload,
        searchOptions: isEmpty(action.payload) ? [] : state.searchOptions,
        searching: !isEmpty(action.payload),
      }

    default:
      return state
  }
}

export function transformSearchData(data) {
  const result = []
  data.subscribers.forEach((subscriber) => {
    result.push({
      id: subscriber.id,
      name: `${subscriber.firstName} ${subscriber.lastName}`,
      value: subscriber.id,
      email: subscriber.email,
      groupName: null,
      subscribersCount: 1,
      label: `${subscriber.firstName} ${subscriber.lastName}`,
      phoneNumber: subscriber.phoneNumber
        ? `${subscriber.countryCode}${subscriber.phoneNumber}`
        : '',
      hideContacts: subscriber.hideContacts,
      lastInvitationStatus: subscriber.lastInvitationStatus,
      groupBy: SUBSCRIBERS,
      type: SUBSCRIBERS,
      parentId: null,
      channel: subscriber.channel,
      avatarCharacters: subscriber.avatarCharacters,
      avatar: subscriber.avatar,
    })
  })

  data.groups.forEach((group) => {
    group.tags.forEach((tag) => {
      if (tag.subscriberCount > 0) {
        result.push({
          id: tag.id,
          name: `${group.name} -> ${tag.name}`,
          value: tag.id,
          type: SUB_GROUP,
          groupBy: 'groups',
          groupName: null,
          tags: null,
          parentId: group.id,
          subscribersCount: tag.subscriberCount,
          channel: null,
        })
      }
    })
  })

  return result
}

export const useRecipients = () => {
  const [state, dispatch] = useReducer(reducer, initialValue())
  const dispatchSystem = useDispatch()
  const { formatMessage } = useIntl()
  const { isGranted: isAdmin } = useAccessControl([AccessType.ADMINISTRATION])

  function transformData(data) {
    const groupByLabel = formatMessage({
      id: 'createNotification.dialog.recipient.groups.title',
    })
    const options = []

    const notInGroup = data.groups.find(
      (item) =>
        item.type === SUBSCRIBER_WITHOUT_GROUP &&
        Number(item.subscribersCount > 0),
    )

    const everyoneGroup = data.groups.find(
      (g) => g.type === EVERYONE && Number(g.subscribersCount > 0),
    )
    if (everyoneGroup && everyoneGroup.tags.length > 0 && isAdmin) {
      options.push({
        id: everyoneGroup.tags[0].id,
        value: everyoneGroup.tags[0].id,
        name: formatMessage({ id: 'recipient.selection.everyone' }),
        label: formatMessage({ id: 'recipient.selection.everyone' }),
        type: EVERYONE_NAME,
        groupName: null,
        subscribersCount: everyoneGroup.tags[0].subscribersCount,
        tags: null,
        parentId: null,
        groupBy: groupByLabel,
        channel: null,
      })
    }

    //count incorrect use only check have subscriber
    const groupCount = data.groups
      .filter((item) => item.type === 0)
      .reduce((sum, item) => sum + Number(item.subscribersCount || 0), 0)

    const noGroup = data.groups.find(
      (g) =>
        g.type === SUBSCRIBER_WITHOUT_GROUP && Number(g.subscribersCount > 0),
    )

    if (
      noGroup &&
      noGroup.tags.length > 0 &&
      Number(notInGroup.subscribersCount) > 0
    ) {
      options.push({
        id: noGroup.tags[0].id,
        name: formatMessage({ id: 'group.subscribersNotInGroup.title' }),
        value: noGroup.tags[0].id,
        label: formatMessage({ id: 'group.subscribersNotInGroup.title' }),
        email: '',
        type: WITHOUT_GROUP_NAME,
        groupName: null,
        subscribersCount: noGroup.tags[0].subscribersCount,
        tags: null,
        parentId: null,
        groupBy: groupByLabel,
        channel: null,
      })
    }

    if (groupCount > 0) {
      options.push({
        id: EVERY_GROUP_NAME,
        name: formatMessage({ id: 'subscriber.groups.list.all' }),
        value: EVERY_GROUP_NAME,
        label: formatMessage({ id: 'subscriber.groups.list.all' }),
        email: '',
        type: EVERY_GROUP_NAME,
        groupName: null,
        subscribersCount:
          Number(data.subscriberCountInAllGroupsBesideDefaultGroups) ?? 0,
        tags: null,
        parentId: null,
        groupBy: groupByLabel,
        channel: null,
      })
    }

    data.groups
      .filter((g) => g.type === NORMAL)
      .forEach((group) => {
        const groupTags = group.tags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          value: tag.id,
          label: tag.name,
          group_id: group.id,
          subscribersCount: tag.subscribersCount,
          channel: null,
        }))

        const matchTerms = group.tags.map((tag) => tag.id)

        if (group.subscribersCount > 0) {
          options.push({
            id: group.id,
            name: group.name,
            value: group.id,
            label: group.name,
            type: GROUP,
            groupName: null,
            subscribersCount: group.subscribersCount,
            tags: groupTags,
            parentId: null,
            matchTerms,
            groupBy: groupByLabel,
            channel: null,
          })

          const sortTags = sortBy(compose(toLower, prop('name')), group.tags)

          sortTags.forEach((tag) => {
            if (Number(tag.subscribersCount) > 0) {
              options.push({
                id: tag.id,
                name: tag.name,
                value: tag.id,
                label: tag.name,
                email: '',
                type: SUB_GROUP,
                groupName: group.name,
                subscribersCount: tag.subscribersCount,
                tags: null,
                parentId: group.id,
                groupBy: groupByLabel,
                channel: null,
              })
            }
          })
        }
      })

    return options
  }

  const getOptions = useCallback(({ isBeacon }) => {
    dispatch({
      type: actionMessageType.START_LOADING,
    })
    effects.apis
      .getGroupWithSubscriber({
        type: join(',', [NORMAL, EVERYONE, SUBSCRIBER_WITHOUT_GROUP]),
        status: join(',', [
          PLACE_INVITATION_ACCEPT_REQUEST,
          PLACE_INVITATION_ACCEPT_INVITATION,
          PLACE_INVITATION_PUBLIC_SUBSCRIBE,
        ]),
        scope: isBeacon ? SCOPE_BEACON : SCOPE_GENERAL,
      })
      .then((response) => {
        dispatch({
          type: actionMessageType.FETCH_SUCCESS,
          payload: transformData(response),
        })
      })
      .catch((error) => {
        dispatchSystem(
          MessageAlert.showError({
            message: formatMessage({ id: 'error.internet.server.error' }),
          }),
        )
      })
  }, [])

  const setSearchText = (text) => {
    dispatch({ type: actionMessageType.SET_SEARCH_TEXT, payload: text })
  }

  const onSearchRecipient = useCallback(
    debounce((value, scope) => {
      if (!isEmpty(value) && !equals(value, state.searchText)) {
        effects.apis.searchRecipient(value, scope).then((response) => {
          dispatch({
            type: actionMessageType.SET_SEARCH_RECIPIENT,
            value: transformSearchData(response),
          })
        })
      }
    }, TIMEOUT_SEARCH),
    [],
  )

  const checkEveryone = (valueIds) => {
    const allGroupAndNotInGroupIds = state.options
      .filter(
        (item) =>
          !includes(item.type, [EVERYONE_NAME, EVERY_GROUP_NAME, GROUP]),
      )
      .map((item) => item.id)
    return checkEquals(allGroupAndNotInGroupIds, valueIds)
  }

  const mapNewValue = (valueOption, valueForm) => {
    const groups = state.options.filter((opt) => opt.type === GROUP)

    const selectedValuesMap = new Map(
      pipe(
        filter(propSatisfies((type) => type !== SUBSCRIBERS, 'type')),
        map((v) => [v.value, v]),
      )(valueOption),
    )

    const result = []

    for (const group of groups) {
      const subGroupIds = group.matchTerms || []

      const allSubGroupsSelected = subGroupIds.every((id) =>
        selectedValuesMap.has(id),
      )

      if (allSubGroupsSelected) {
        result.push(group)

        subGroupIds.forEach((id) => selectedValuesMap.delete(id))
      }
    }

    for (const item of selectedValuesMap.values()) {
      result.push(item)
    }

    return valueForm.subscribers.length > 0
      ? [...result, ...valueForm.subscribers]
      : result
  }

  return {
    state,
    getOptions,
    onSearchRecipient,
    setSearchText,
    checkEveryone,
    mapNewValue,
  }
}
